import React, { useEffect, useMemo, useState } from 'react';
import {
  listBusinessesForAdmin,
  STATUS_FILTER_OPTIONS,
  STATUS_FILTER_ALL,
  pillClassForStatus,
  labelForStatus,
} from '../lib/businesses.js';
import ActivationModal from './ActivationModal.jsx';
import './BusinessesPage.css';

// ============================================================
// BusinessesPage — Session 2 (Phase 2).
//
// Read-only list of all /businesses with filter chips, client-side search
// over the loaded page, cursor-based pagination, and a read-only detail
// drawer on row click. No write operations this session — the Verify /
// Suspend / Activate / Reactivate actions belong to the subscription-
// model session (own session, involves ₹199/₹499/₹899 activation flow
// with owner notifications).
//
// FIRESTORE COST PER SESSION (typical admin use):
//   Page mount:      25 reads
//   Filter change:   25 reads (new query)
//   Load more:       25 reads per click
//   Search typing:   0 (client-side filter over loaded page)
//   Row → detail:    0 (data already in memory)
//
// Bounded, predictable, matches Phase 2 cost discipline.
// ============================================================

// Client-side search matcher — case-insensitive substring across the
// fields an admin is likely to search (name, phone, ownerId, email,
// slug, city). Loaded page only; no query hits Firestore.
function matchesSearch(biz, term) {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    biz.name,
    biz.phone,
    biz.ownerId,
    biz.email,
    biz.slug,
    biz.city,
    biz.shortDescription,
    biz.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

// Format a Firestore Timestamp (or plain Date, or ISO string) for display.
// Returns '—' for null/missing.
function fmtDate(ts) {
  if (!ts) return '—';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts === 'string') d = new Date(ts);
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Human-readable rendering for a field value in the detail drawer.
// Handles Timestamps, arrays, booleans, maps, and primitives. Uses
// JSON.stringify for nested objects so admins can see structure without
// us hand-coding every possible schema field.
function renderFieldValue(v) {
  if (v == null || v === '') return <span className="sa-detail-empty">—</span>;
  if (typeof v?.toDate === 'function') return fmtDate(v);
  if (typeof v?.seconds === 'number' && typeof v?.nanoseconds === 'number') return fmtDate(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="sa-detail-empty">—</span>;
  if (typeof v === 'object') return <pre className="sa-detail-json">{JSON.stringify(v, null, 2)}</pre>;
  if (typeof v === 'string' && v.startsWith('http')) {
    return (
      <a href={v} target="_blank" rel="noopener noreferrer" className="sa-detail-link">
        {v.length > 60 ? v.slice(0, 60) + '…' : v}
      </a>
    );
  }
  return String(v);
}

export default function BusinessesPage({ authUser }) {
  const [businesses, setBusinesses] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [search, setSearch] = useState('');
  const [selectedBiz, setSelectedBiz] = useState(null);

  // Session 6a — Activation modal is opened from the detail drawer.
  // activationBiz is the business to activate; null when modal closed.
  const [activationBiz, setActivationBiz] = useState(null);

  // Load first page on mount and whenever the filter changes. Clears
  // any accumulated pages so switching filters doesn't stack mismatched
  // results.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBusinesses([]);
    setLastDoc(null);
    setHasMore(false);

    listBusinessesForAdmin({ statusFilter, cursor: null })
      .then((res) => {
        if (cancelled) return;
        setBusinesses(res.businesses);
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[BusinessesPage] initial load failed:', err);
        setError(err?.message || 'Failed to load businesses');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const res = await listBusinessesForAdmin({ statusFilter, cursor: lastDoc });
      setBusinesses((prev) => [...prev, ...res.businesses]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('[BusinessesPage] load-more failed:', err);
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  // On successful activation: refetch the current page so the drawer + list
  // reflect the new activation fields. Also refresh the selected biz so
  // the drawer's subscription section shows the new state without needing
  // a manual close/reopen. 25 reads — bounded, discrete action, matches
  // Session 6a cost discipline.
  const handleActivationSuccess = async () => {
    setActivationBiz(null);
    try {
      const res = await listBusinessesForAdmin({ statusFilter, cursor: null });
      setBusinesses(res.businesses);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
      // Update the selectedBiz to the fresh version so the drawer's
      // Subscription section reflects the new activation.
      if (selectedBiz) {
        const fresh = res.businesses.find((b) => b.id === selectedBiz.id);
        if (fresh) setSelectedBiz(fresh);
      }
    } catch (err) {
      console.error('[BusinessesPage] post-activation refetch failed:', err);
      setError(err?.message || 'Refetch failed');
    }
  };

  // Client-side search filter over the loaded page. Memoized so typing
  // doesn't re-filter unless the search term or loaded list actually
  // changed.
  const visibleBusinesses = useMemo(
    () => businesses.filter((b) => matchesSearch(b, search)),
    [businesses, search],
  );

  return (
    <>
      <div className="sa-filter-bar">
        {STATUS_FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.slug || 'all'}
            className={`sa-filter-chip ${statusFilter === opt.slug ? 'active' : ''}`}
            onClick={() => setStatusFilter(opt.slug)}
            type="button"
          >
            {opt.label}
          </button>
        ))}
        <input
          type="text"
          className="sa-filter-search"
          placeholder="Search by name, phone, owner, city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && (
        <div className="sa-error-banner">
          <b>Couldn't load businesses.</b> {error}
        </div>
      )}

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Business</th>
              <th>Category</th>
              <th>City</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="sa-table-empty">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && visibleBusinesses.length === 0 && (
              <tr>
                <td colSpan={6} className="sa-table-empty">
                  {search
                    ? `No results for "${search}" on this page. Try Load more or a different filter.`
                    : businesses.length === 0
                      ? 'No businesses match this filter.'
                      : 'No results.'}
                </td>
              </tr>
            )}

            {!loading &&
              visibleBusinesses.map((biz) => (
                <tr key={biz.id} onClick={() => setSelectedBiz(biz)}>
                  <td>
                    <div className="sa-table-row-bizname">
                      <div className="sa-table-logo">{biz.logo || '🏪'}</div>
                      <div className="sa-table-bizname-text">
                        <div className="sa-table-bizname-primary">{biz.name || '—'}</div>
                        {biz.shortDescription && (
                          <div className="sa-table-bizname-secondary">{biz.shortDescription}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{biz.category || '—'}</td>
                  <td>{biz.city || '—'}</td>
                  <td className="sa-table-owner">{biz.ownerId || '—'}</td>
                  <td>
                    <span className={`sa-pill ${pillClassForStatus(biz.status)}`}>
                      {labelForStatus(biz.status)}
                    </span>
                  </td>
                  <td>{fmtDate(biz.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && hasMore && (
        <div className="sa-load-more-wrap">
          <button className="sa-btn" onClick={handleLoadMore} disabled={loadingMore} type="button">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {selectedBiz && (
        <BusinessDetailDrawer
          biz={selectedBiz}
          onClose={() => setSelectedBiz(null)}
          onManageSubscription={() => setActivationBiz(selectedBiz)}
        />
      )}

      {activationBiz && (
        <ActivationModal
          biz={activationBiz}
          adminUid={authUser?.uid}
          onClose={() => setActivationBiz(null)}
          onSuccess={handleActivationSuccess}
        />
      )}
    </>
  );
}

// ============================================================
// BusinessDetailDrawer — read-only side panel showing every field on
// the business doc. Deliberately generic (iterates over doc keys) so
// schema additions in future sessions show up automatically without
// updating this component.
//
// The subscription-model session will replace this with a proper
// edit-capable detail view. For now: read-only, no action buttons.
// ============================================================

// Field ordering for the detail drawer: known-important fields first
// in a stable order, then everything else alphabetical. Keeps common
// fields at eye-level while still surfacing anything we didn't hard-code.
const DETAIL_TOP_FIELDS = [
  'name', 'slug', 'status', 'category', 'city', 'ownerId', 'phone',
  'email', 'shortDescription', 'description', 'createdAt', 'updatedAt',
  'verified', 'activated', 'fssaiVerified', 'trialStartedAt', 'trialDays',
  'activatedAt', 'activatedBy', 'activationTier', 'activationAmount',
  'activationDurationMonths', 'activationValidTill', 'activationValidTillOverride',
  'activationPaymentRef', 'activationNote',
  'agpPartnerId', 'connectCount', 'orderCount',
];

function BusinessDetailDrawer({ biz, onClose, onManageSubscription }) {
  const allKeys = Object.keys(biz).filter((k) => k !== 'id');
  const topKeys = DETAIL_TOP_FIELDS.filter((k) => allKeys.includes(k));
  const restKeys = allKeys.filter((k) => !DETAIL_TOP_FIELDS.includes(k)).sort();
  const orderedKeys = [...topKeys, ...restKeys];

  return (
    <>
      <div className="sa-drawer-backdrop" onClick={onClose} />
      <div className="sa-detail-panel open" role="dialog" aria-modal="true">
        <div className="sa-detail-panel-header">
          <div className="sa-detail-panel-title-block">
            <div className="sa-detail-panel-title">{biz.name || 'Business'}</div>
            <div className="sa-detail-panel-sub">
              <span className={`sa-pill ${pillClassForStatus(biz.status)}`}>
                {labelForStatus(biz.status)}
              </span>{' '}
              · ID {biz.id}
            </div>
          </div>
          <button
            className="sa-detail-panel-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="sa-detail-panel-body">
          {/* Session 6a: Subscription section shows current activation
              state (or trial state if not yet activated) plus a Manage
              button that opens the Activation modal. */}
          <SubscriptionSection biz={biz} onManage={onManageSubscription} />

          <div className="sa-detail-block">
            <div className="sa-detail-block-title">All fields</div>
            {orderedKeys.map((key) => (
              <div className="sa-detail-row" key={key}>
                <div className="sa-detail-row-label">{key}</div>
                <div className="sa-detail-row-value">{renderFieldValue(biz[key])}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// SubscriptionSection — Session 6a.
//
// Shows the business's current activation/trial state at the top of
// the drawer, along with a Manage subscription button that opens the
// Activation modal. Reads all state from the biz doc (activated,
// activationValidTill, trialStartedAt, trialDays) — zero extra reads.
//
// STATE LOGIC:
//   activated && validTill > now:  "Active until DD MMM YYYY" (green)
//   activated && validTill <= now: "Expired on DD MMM YYYY" (grey)
//   !activated && trial > now:     "Trial ends DD MMM YYYY"
//   !activated && trial <= now:    "Trial expired on DD MMM YYYY"
//   Otherwise:                     "Not activated"
// ============================================================
function SubscriptionSection({ biz, onManage }) {
  const now = new Date();

  const validTill = biz.activationValidTill?.toDate?.() || null;
  const trialStart = biz.trialStartedAt?.toDate?.() || null;
  const trialDays = typeof biz.trialDays === 'number' ? biz.trialDays : 0;
  const trialEnd = trialStart && trialDays > 0
    ? new Date(trialStart.getTime() + trialDays * 24 * 60 * 60 * 1000)
    : null;

  let state;
  let stateClass;
  let detail = null;

  if (biz.activated && validTill && validTill > now) {
    state = `Active until ${fmtDate(validTill)}`;
    stateClass = 'sa-sub-active';
    detail = `Tier ₹${biz.activationAmount ?? '—'} · ${biz.activationDurationMonths ?? '—'} month${biz.activationDurationMonths === 1 ? '' : 's'}`;
  } else if (biz.activated && validTill) {
    state = `Expired on ${fmtDate(validTill)}`;
    stateClass = 'sa-sub-expired';
    detail = `Last tier ₹${biz.activationAmount ?? '—'} · ${biz.activationDurationMonths ?? '—'} month${biz.activationDurationMonths === 1 ? '' : 's'}`;
  } else if (trialEnd && trialEnd > now) {
    state = `Trial ends ${fmtDate(trialEnd)}`;
    stateClass = 'sa-sub-trial';
  } else if (trialEnd) {
    state = `Trial expired on ${fmtDate(trialEnd)}`;
    stateClass = 'sa-sub-expired';
  } else {
    state = 'Not activated';
    stateClass = 'sa-sub-inactive';
  }

  const buttonLabel = biz.activated ? 'Manage subscription' : 'Activate business';

  return (
    <div className="sa-detail-block sa-sub-block">
      <div className="sa-detail-block-title">Subscription</div>
      <div className={`sa-sub-state ${stateClass}`}>
        <div className="sa-sub-state-main">{state}</div>
        {detail && <div className="sa-sub-state-detail">{detail}</div>}
      </div>
      {biz.activationPaymentRef && (
        <div className="sa-sub-meta">
          Last payment ref: <code>{biz.activationPaymentRef}</code>
          {biz.activationValidTillOverride && (
            <> · <b>Valid Till override applied</b></>
          )}
        </div>
      )}
      <button
        type="button"
        className="sa-btn sa-btn-primary sa-sub-cta"
        onClick={onManage}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
